// src/pages/AccountVendors.js
// Account-scoped vendor roster (cake, photo, DJ, etc.). Mirrors
// /account/team in shape — table-style listing + inline-add form, all on
// one page. Phase E (2026-05-22).

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { confirm, toast } from '../components/Shared/feedback';

const CATEGORIES = [
	{ value: 'cake', label: 'Cake' },
	{ value: 'photo', label: 'Photography' },
	{ value: 'video', label: 'Videography' },
	{ value: 'dj', label: 'DJ' },
	{ value: 'mc', label: 'MC' },
	{ value: 'decor', label: 'Decor' },
	{ value: 'flowers', label: 'Flowers' },
	{ value: 'makeup', label: 'Makeup' },
	{ value: 'aso_ebi', label: 'Aso-ebi' },
	{ value: 'security', label: 'Security' },
	{ value: 'logistics', label: 'Logistics' },
	{ value: 'rentals', label: 'Rentals' },
	{ value: 'other', label: 'Other' },
];
const CATEGORY_LABEL = CATEGORIES.reduce((acc, c) => ({ ...acc, [c.value]: c.label }), {});

const EMPTY_FORM = {
	name: '', category: 'other', contactName: '', phone: '', email: '',
	instagram: '', website: '', notes: '', defaultRateNgn: '',
};

const AccountVendors = () => {
	const [vendors, setVendors] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [form, setForm] = useState(EMPTY_FORM);
	const [editingId, setEditingId] = useState(null);
	const [saving, setSaving] = useState(false);

	const load = async () => {
		setLoading(true);
		try {
			const res = await api.get('/vendors');
			setVendors(res.data?.vendors || []);
		} catch (err) {
			setError(err.response?.data?.msg || 'Failed to load vendors.');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => { load(); }, []);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!form.name.trim()) {
			toast.error('Vendor name is required.');
			return;
		}
		setSaving(true);
		try {
			if (editingId) {
				await api.put(`/vendors/${editingId}`, form);
				toast.success('Vendor updated.');
			} else {
				await api.post('/vendors', form);
				toast.success('Vendor added.');
			}
			setForm(EMPTY_FORM);
			setEditingId(null);
			await load();
		} catch (err) {
			toast.error(err.response?.data?.msg || 'Save failed.');
		} finally {
			setSaving(false);
		}
	};

	const handleEdit = (v) => {
		setEditingId(v._id);
		setForm({
			name: v.name || '',
			category: v.category || 'other',
			contactName: v.contactName || '',
			phone: v.phone || '',
			email: v.email || '',
			instagram: v.instagram || '',
			website: v.website || '',
			notes: v.notes || '',
			defaultRateNgn: v.defaultRateNgn ?? '',
		});
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const handleCancelEdit = () => {
		setEditingId(null);
		setForm(EMPTY_FORM);
	};

	const handleRemove = async (vendor) => {
		const ok = await confirm({
			title: `Remove "${vendor.name}"?`,
			message: 'This will also untag them from any events. Their record is gone for good.',
			tone: 'danger',
			confirmLabel: 'Remove',
		});
		if (!ok) return;
		try {
			await api.delete(`/vendors/${vendor._id}`);
			toast.success('Vendor removed.');
			setVendors((prev) => prev.filter((v) => v._id !== vendor._id));
		} catch (err) {
			toast.error('Failed to remove vendor.');
		}
	};

	return (
		<div className="ev-user-shell">
			<header className="ev-admin-topbar">
				<div className="ev-admin-topbar-inner">
					<div className="ev-admin-topbar-left">
						<div className="ev-brand-lockup">
							<img src="/images/eventory-logo.svg" alt="Eventory" />
						</div>
					</div>
					<div className="ev-admin-topbar-actions">
						<Link to="/dashboard" className="ev-soft-btn">My events</Link>
						<Link to="/account/team" className="ev-soft-btn">Team</Link>
						<Link to="/account" className="ev-soft-btn">Account</Link>
					</div>
				</div>
			</header>

			<main className="ev-user-dash">
				<div className="ev-user-wrap">
					<div className="ev-admin-header">
						<div>
							<h2 className="ev-section-title">My vendors</h2>
							<div className="ev-subtle">
								Tag who supplied the cake, photography, DJ, etc. on each event.
								Builds up a private vendor list you can re-use — and will power the marketplace when Phase 2 ships.
							</div>
						</div>
					</div>

					{error && <div className="ev-alert ev-alert-danger">{error}</div>}

					<section className="ev-admin-section ev-flat-card">
						<h3 className="ev-zero-margin">{editingId ? 'Edit vendor' : 'Add a vendor'}</h3>
						<form onSubmit={handleSubmit} className="ev-stack" style={{ gap: 12, marginTop: 8 }}>
							<div className="ev-field-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
								<div className="ev-field-block">
									<label htmlFor="v-name">Business name *</label>
									<input id="v-name" type="text" className="ev-input" value={form.name}
										onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
										placeholder="e.g. Bella's Cakes" required />
								</div>
								<div className="ev-field-block">
									<label htmlFor="v-category">Category</label>
									<select id="v-category" className="ev-input" value={form.category}
										onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
										{CATEGORIES.map((c) => (
											<option key={c.value} value={c.value}>{c.label}</option>
										))}
									</select>
								</div>
								<div className="ev-field-block">
									<label htmlFor="v-contact">Contact name</label>
									<input id="v-contact" type="text" className="ev-input" value={form.contactName}
										onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))} />
								</div>
								<div className="ev-field-block">
									<label htmlFor="v-phone">Phone</label>
									<input id="v-phone" type="tel" className="ev-input" value={form.phone}
										onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
								</div>
								<div className="ev-field-block">
									<label htmlFor="v-email">Email</label>
									<input id="v-email" type="email" className="ev-input" value={form.email}
										onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
								</div>
								<div className="ev-field-block">
									<label htmlFor="v-ig">Instagram handle</label>
									<input id="v-ig" type="text" className="ev-input" value={form.instagram}
										onChange={(e) => setForm((f) => ({ ...f, instagram: e.target.value }))}
										placeholder="@bellascakes" />
								</div>
								<div className="ev-field-block">
									<label htmlFor="v-rate">Default rate (₦, optional)</label>
									<input id="v-rate" type="number" min="0" className="ev-input" value={form.defaultRateNgn}
										onChange={(e) => setForm((f) => ({ ...f, defaultRateNgn: e.target.value }))} />
								</div>
								<div className="ev-field-block">
									<label htmlFor="v-website">Website (optional)</label>
									<input id="v-website" type="url" className="ev-input" value={form.website}
										onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} />
								</div>
							</div>
							<div className="ev-field-block">
								<label htmlFor="v-notes">Notes</label>
								<textarea id="v-notes" rows={2} className="ev-input" value={form.notes}
									onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
									placeholder="e.g. Best for traditional weddings; gives planner discount at 5+ events" />
							</div>
							<div className="ev-button-row" style={{ gap: 8 }}>
								<button type="submit" className="ev-primary-btn" disabled={saving}>
									{saving ? 'Saving…' : editingId ? 'Save changes' : 'Add vendor'}
								</button>
								{editingId && (
									<button type="button" className="ev-soft-btn" onClick={handleCancelEdit}>Cancel</button>
								)}
							</div>
						</form>
					</section>

					<section className="ev-admin-section ev-flat-card">
						<h3 className="ev-zero-margin">Roster ({vendors.length})</h3>
						{loading ? (
							<div className="ev-subtle ev-top-gap">Loading…</div>
						) : vendors.length === 0 ? (
							<div className="ev-empty ev-top-gap">
								<strong>No vendors yet.</strong>
								<div className="ev-subtle">Add one above. Tagged vendors show up on each event's overview, and you can re-use them across events.</div>
							</div>
						) : (
							<table className="ev-data-table ev-top-gap">
								<thead>
									<tr>
										<th>Vendor</th>
										<th>Category</th>
										<th>Contact</th>
										<th>Used in</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									{vendors.map((v) => (
										<tr key={v._id}>
											<td>
												<strong>{v.name}</strong>
												{v.notes && <div className="ev-subtle" style={{ fontSize: 12 }}>{v.notes}</div>}
											</td>
											<td>{CATEGORY_LABEL[v.category] || v.category}</td>
											<td>
												{v.contactName && <div>{v.contactName}</div>}
												{v.phone && <div className="ev-subtle" style={{ fontSize: 12 }}>{v.phone}</div>}
												{v.instagram && <div className="ev-subtle" style={{ fontSize: 12 }}>{v.instagram}</div>}
											</td>
											<td>{v.eventCount} event{v.eventCount === 1 ? '' : 's'}</td>
											<td>
												<button type="button" className="ev-soft-btn" onClick={() => handleEdit(v)}>Edit</button>{' '}
												<button type="button" className="ev-soft-btn" onClick={() => handleRemove(v)}>Remove</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</section>
				</div>
			</main>
		</div>
	);
};

export default AccountVendors;
